// ─── Help Guide Content ─────────────────────────────────────────────────────
// Scripted (not AI) — every entry here is written by hand, matched by
// keyword. `roles` filters which users see a topic in quick-suggestions;
// omit `roles` to show it to everyone. `action` (optional) adds a button
// that jumps straight to that view.

const ALL_ROLES = ["principal_broker", "associate_broker", "realtor"];
const BROKER_ROLES = ["principal_broker", "associate_broker"];

const HELP_TOPICS = [
  // ── Getting around ──────────────────────────────────────────────────────
  {
    id: "nav_overview",
    category: "Getting Around",
    question: "What do Dashboard, Pipeline, and Transactions show me?",
    keywords: ["dashboard", "pipeline", "transactions", "views", "navigate", "overview", "difference between"],
    answer: "Dashboard gives you stats, upcoming deadlines, and (for brokers) things needing your attention like a review queue. Pipeline shows every transaction as a card on a board, grouped by stage — you can drag a card between stages. Transactions is a plain sortable/filterable table of everything, best for finding a specific deal quickly.",
    roles: ALL_ROLES,
  },
  {
    id: "nav_switch",
    category: "Getting Around",
    question: "How do I switch between views?",
    keywords: ["switch view", "change view", "menu", "navigation bar", "tabs"],
    answer: "Use the navigation buttons in the top bar — Dashboard, Pipeline, and Transactions. Your current view is highlighted.",
    roles: ALL_ROLES,
  },

  // ── Transactions ─────────────────────────────────────────────────────────
  {
    id: "tx_add",
    category: "Transactions",
    question: "How do I add a new transaction?",
    keywords: ["add transaction", "new transaction", "create transaction", "add deal", "new deal", "start a transaction"],
    answer: "Click the \"+ New Transaction\" button (top right, visible from any view). Fill in the address, side (Buyer/Seller), client/party details, and price, then save — the standard document checklist is added automatically.",
    roles: ALL_ROLES,
    action: { label: "Go to Transactions", view: "list" },
  },
  {
    id: "tx_ai_import",
    category: "Transactions",
    question: "How does Transaction Genie work?",
    keywords: ["transaction genie", "genie", "ai import", "import transaction", "import from documents", "upload contract", "auto fill", "extract", "scan documents"],
    answer: "Click \"🧞 Transaction Genie\" (next to \"+ New Transaction\") and drop in the contract, disclosures, and other deal documents — it reads them with AI, fills in the transaction details, and guesses which checklist item each file belongs to. Nothing is saved until you review and confirm it, and uploading a document never automatically marks its checklist item complete — that's still on you. Always double-check what the genie found against the actual documents before creating the transaction; AI-extracted data may not always be accurate. This is a paid add-on — if you don't see the button, your brokerage hasn't purchased it yet.",
    roles: ALL_ROLES,
    action: { label: "Go to Dashboard", view: "dashboard" },
  },
  {
    id: "tx_details",
    category: "Transactions",
    question: "Where do I see a transaction's details?",
    keywords: ["transaction details", "view details", "see details", "open transaction", "checklist", "where are documents"],
    answer: "Click any transaction card (Pipeline) or row (Transactions list) to open its checklist — that's where you see every required document, upload files, and track progress. Click \"Edit\" instead to change the address, price, parties, or other core fields.",
    roles: ALL_ROLES,
  },
  {
    id: "tx_stages",
    category: "Transactions",
    question: "What are the different transaction stages?",
    keywords: ["stages", "stage mean", "pipeline stages", "pre-listing", "active mls", "under contract", "clear to close"],
    answer: "Pre-Listing/Pre-Offer → Active/MLS → Under Contract → Inspection → Appraisal → Clear to Close → Closed. A deal can also move to Fallen Through if it doesn't complete. These are separate from the review status (draft/submitted/approved) — stage tracks where the deal is in real life, review status tracks where it is in your brokerage's approval process.",
    roles: ALL_ROLES,
  },
  {
    id: "tx_move_stage",
    category: "Transactions",
    question: "How do I move a transaction to a different stage?",
    keywords: ["move stage", "change stage", "drag", "update stage"],
    answer: "In Pipeline view, drag a transaction's card from one stage column and drop it in another.",
    roles: ALL_ROLES,
    action: { label: "Go to Pipeline", view: "pipeline" },
  },
  {
    id: "tx_search",
    category: "Transactions",
    question: "How do I search or filter transactions?",
    keywords: ["search", "filter", "find transaction", "sort"],
    answer: "On the Transactions page, use the search box (matches address or client name) plus the Stage/Side/Status filters. Click any column header in the table to sort by it.",
    roles: ALL_ROLES,
    action: { label: "Go to Transactions", view: "list" },
  },
  {
    id: "tx_archive",
    category: "Transactions",
    question: "How do I archive or delete a transaction?",
    keywords: ["archive", "delete transaction", "remove transaction", "permanently delete"],
    answer: "Only a principal broker can archive a transaction (button on its row) or permanently delete one — deleting requires archiving it first, then removing it from the Archive panel. This is intentional: it's rare and meant to be deliberate.",
    roles: BROKER_ROLES,
  },
  {
    id: "tx_void",
    category: "Transactions",
    question: "What happens if a deal falls through?",
    keywords: ["fell through", "falls through", "void", "cancel transaction", "deal died"],
    answer: "While a transaction is still in Draft or Changes Requested, its owning agent can \"Void\" it (with a reason) — this marks it Fallen Through and locks it. A broker can also drag a card straight to the \"Fallen Through\" column at any stage.",
    roles: ALL_ROLES,
  },

  // ── Checklist & Documents ────────────────────────────────────────────────
  {
    id: "docs_upload",
    category: "Checklist & Documents",
    question: "How do I upload a document?",
    keywords: ["upload document", "upload file", "attach document", "add document"],
    answer: "Open the transaction's checklist, find the item you need (e.g. \"Contract\"), and click to expand it — there's an upload area right there. A task marked Required is automatically checked off the moment a document is attached.",
    roles: ALL_ROLES,
  },
  {
    id: "docs_cant_edit",
    category: "Checklist & Documents",
    question: "Why can't I upload or edit a document?",
    keywords: ["can't upload", "locked", "cannot edit", "read only", "why locked"],
    answer: "Once a transaction is Submitted, Approved, Closed, or Fallen Through, a realtor can no longer edit its checklist — it's locked until a broker requests changes (or, if it's closed/fallen through, until a broker reopens it). Brokers themselves aren't locked out.",
    roles: ALL_ROLES,
  },
  {
    id: "docs_mandatory",
    category: "Checklist & Documents",
    question: "What's the difference between mandatory and optional checklist items?",
    keywords: ["mandatory", "required document", "optional checklist", "required vs optional"],
    answer: "Mandatory items must have a document attached before the transaction can be submitted for review or closed. Optional items can be checked off with or without a document — useful for things that don't always apply.",
    roles: ALL_ROLES,
  },
  {
    id: "docs_custom",
    category: "Checklist & Documents",
    question: "How do I add a custom checklist item to one transaction?",
    keywords: ["custom checklist item", "add item to checklist", "extra document"],
    answer: "Inside that transaction's checklist, there's an \"Add Custom Task\" option — use it for something specific to that one deal. To change what's added to *every* new transaction, see the Checklist Templates setting instead.",
    roles: ALL_ROLES,
  },

  // ── Review workflow ──────────────────────────────────────────────────────
  {
    id: "review_submit",
    category: "Review Workflow",
    question: "How do I submit a transaction for review?",
    keywords: ["submit for review", "submit transaction", "send for approval"],
    answer: "Once every mandatory checklist item has a document attached, a \"Submit for Review\" button appears on the transaction — your broker gets notified and reviews it from their Dashboard.",
    roles: ALL_ROLES,
  },
  {
    id: "review_ready_to_close",
    category: "Review Workflow",
    question: "What does \"Ready to Close\" mean?",
    keywords: ["ready to close", "clear to close meaning"],
    answer: "It's the panel on a broker's Dashboard listing every Approved transaction — deals that just need their closing paperwork finished. If a deal still has missing required documents, it shows what's missing instead of a Close button.",
    roles: ALL_ROLES,
  },
  {
    id: "review_approve",
    category: "Review Workflow",
    question: "How do I review or approve a submitted transaction?",
    keywords: ["review transaction", "approve transaction", "request changes"],
    answer: "Submitted transactions appear in your Dashboard's Review Queue. Open one to review its documents, then either Approve it or Request Changes (with a note back to the agent).",
    roles: BROKER_ROLES,
  },
  {
    id: "review_close",
    category: "Review Workflow",
    question: "How do I close a transaction?",
    keywords: ["close transaction", "close a deal", "finish closing"],
    answer: "Once a transaction is Approved with all mandatory documents attached, click \"Close\" from its Ready to Close card or its own action bar. Principal brokers on their own deals get a one-click \"Approve & Close\" instead, since there's no one above them to review it.",
    roles: BROKER_ROLES,
  },

  // ── Team & settings (broker-only) ───────────────────────────────────────
  {
    id: "team_invite",
    category: "Team & Settings",
    question: "How do I invite a new team member?",
    keywords: ["invite user", "invite team member", "add agent", "new realtor", "add user"],
    answer: "Open Team (top nav), click \"+ Add User\", fill in their name/role/email, and an invitation email gets sent for them to set their own password.",
    roles: BROKER_ROLES,
  },
  {
    id: "team_remove",
    category: "Team & Settings",
    question: "How do I remove a team member?",
    keywords: ["remove user", "delete user", "remove team member", "offboard"],
    answer: "Only the principal broker can remove a user, from the Team panel — this permanently deletes their account. Their past transactions stay in the system.",
    roles: ["principal_broker"],
  },
  {
    id: "team_checklist_template",
    category: "Team & Settings",
    question: "How do I customize our checklist template?",
    keywords: ["checklist template", "customize checklist", "edit required documents", "change checklist items"],
    answer: "Open Checklist (top nav, principal broker only) to add, edit, reorder, or deactivate the items that get added to every new transaction. Changing a template only affects transactions created after the change — existing ones keep what they had.",
    roles: ["principal_broker"],
  },
  {
    id: "team_branding",
    category: "Team & Settings",
    question: "How do I update our branding (logo/name)?",
    keywords: ["branding", "logo", "change name", "update logo"],
    answer: "Open Branding (top nav, principal broker only) to update your brokerage's name and upload a logo — it shows up on the login screen and the app header for everyone at your brokerage.",
    roles: ["principal_broker"],
  },

  // ── Commission / reporting ───────────────────────────────────────────────
  {
    id: "commission_view",
    category: "Commission & Reporting",
    question: "Where do I see my commission or GCI?",
    keywords: ["commission", "gci", "earnings", "how much did i make", "net"],
    answer: "Your Dashboard's stat cards show closed and pipeline GCI. The closed-transactions ledger further down breaks it out deal-by-deal, and (for brokers) splits agent vs. brokerage commission per closing.",
    roles: ALL_ROLES,
  },
  {
    id: "scope_toggle",
    category: "Commission & Reporting",
    question: "What's the \"My Transactions\" vs. \"Brokerage/Team\" toggle?",
    keywords: ["my transactions toggle", "brokerage view", "team view", "scope toggle"],
    answer: "Brokers can toggle their Dashboard and Pipeline between just their own transactions and everything they're allowed to see (the whole brokerage for a principal broker, their team for an associate broker). It defaults to \"My Transactions.\"",
    roles: BROKER_ROLES,
  },
];

export function getVisibleTopics(role) {
  return HELP_TOPICS.filter(t => !t.roles || t.roles.includes(role));
}

// Simple keyword scoring: count how many of a topic's keywords appear as a
// substring of the query (case-insensitive), plus a bonus if the query
// substring-matches the topic's own question text.
const STOPWORDS = new Set([
  "a","an","the","i","do","does","did","is","are","to","of","for","how","what",
  "where","when","why","can","could","should","would","in","on","my","me","it",
  "this","that","and","or","with","you","your","we","us","see","get",
]);

function tokenize(str) {
  return str.toLowerCase().split(/[^a-z0-9]+/).filter(w => w && !STOPWORDS.has(w));
}

// Word-overlap matching, not substring matching — "how do I add a transaction"
// needs to match the keyword "add transaction" even though "add a transaction"
// isn't a literal substring of it. A keyword scores highest when ALL of its
// words appear in the query (a real phrase match); a partial word overlap
// still counts for something, so close-but-not-exact phrasing isn't a dead end.
export function matchTopics(query, role) {
  const qWords = new Set(tokenize(query));
  if (qWords.size === 0) return [];
  const candidates = getVisibleTopics(role);
  const scored = candidates.map(topic => {
    let score = 0;
    for (const kw of topic.keywords) {
      const kwWords = tokenize(kw);
      if (kwWords.length === 0) continue;
      const hits = kwWords.filter(w => qWords.has(w)).length;
      if (hits === 0) continue;
      score += hits === kwWords.length ? kwWords.length * 3 : hits;
    }
    const questionHits = tokenize(topic.question).filter(w => qWords.has(w)).length;
    score += questionHits;
    return { topic, score };
  }).filter(r => r.score > 0);
  scored.sort((a, b) => b.score - a.score);
  return scored.map(r => r.topic);
}

export default HELP_TOPICS;
